import { Link, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { BrandMark } from './Brand'
import { UserMenu } from '../auth/UserMenu'
import { AskPanel } from './AskPanel'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'
import './SiteHeader.css'

/**
 * The bar that carries the product's identity and its two destinations.
 *
 * Each screen used to render its own heading, so there was nowhere to click to
 * get out of a filtered register or a half-finished form — the only way home was
 * the browser's Back button, which on the register meant stepping back through
 * every filter change. One mark, always in the same place, always going home.
 *
 * The tabs replaced a "Register" link that sat among the notification bell and
 * the user menu, where it read as an action rather than a place. Tabs say plainly
 * which screens you LIVE IN, and which one you are standing on.
 *
 * There were two for a long time and the comment here said "exactly two". A third
 * arrived with machinery on 2026-09-02, and it belongs: 562 machines are a place
 * you go and stay, not a task you finish. The test for a tab is unchanged — it is
 * somewhere you live, not something you do — so the count was never the rule.
 *
 * Import, report and intake are deliberately NOT tabs. They are things you do
 * and come back from — reached from the screen that starts them — and putting a
 * task beside a destination makes both harder to find.
 *
 * Reminders is not a tab either, for a different reason: it is set up once and
 * then left alone. It now sits one click INSIDE the bell rather than on it — the
 * bell goes to the notifications, which are read daily, and the rules screen is
 * reached from there. The daily thing gets the header slot.
 */
export function SiteHeader() {
  const { pathname, search } = useLocation()

  // The Dashboard tab carries aria-current now, so the mark must not also claim
  // it — two elements announcing themselves as the current page is worse than
  // neither. The mark stays a plain way home.
  const atHome = pathname === '/' && search === ''

  const atNotifications = pathname.startsWith('/notifications')

  /*
    The badge.

    Polled rather than pushed: the poller runs every few hours, so a stale count
    costs nothing and a websocket for a number that moves twice a day would be
    the wrong trade. A minute of staleness is invisible; the query is invalidated
    directly whenever this session clears something.
  */
  const unreadQuery = useQuery({
    queryKey: ['notification-count'],
    queryFn: () => api.getNotificationCount(),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    // A bell that cannot count is still a bell. A failed count must never take
    // the header down with it.
    retry: false,
  })

  const unread = unreadQuery.data?.unreadCount ?? 0

  const [asking, setAsking] = useState(false)

  // The page behind must not scroll while the panel is open, or the scrollbar
  // the user grabs moves the register instead of the answer.
  useBodyScrollLock(asking)

  return (
    <header className="site">
      <Link
        to="/"
        className="site__brand"
        aria-current={atHome ? 'page' : undefined}
        // Named here rather than on the mark, which is aria-hidden — otherwise
        // this reads out as "Prarambh Prarambh". Says dashboard, not register:
        // the mark stopped going to the register when the dashboard took `/`,
        // and a screen reader announcing the old destination would be a lie.
        aria-label="Prarambh — go to the dashboard"
      >
        <BrandMark size={30} className="site__mark" />

        <span className="site__words">
          <span className="site__name">Prarambh</span>
          <span className="site__tag">Compliance register</span>
        </span>
      </Link>

      {/*
        Named for what you get, not for what the code calls it. Internally this
        is "the register"; to somebody arriving it is the list of vehicles, and
        the tab that says so needs no explaining.

        Real links rather than a tab widget: these are routes, so they must be
        openable in a new tab, shareable, and restorable by Back.
      */}
      <nav className="site__tabs" aria-label="Main">
        <Link
          to="/"
          className={`site__tab${pathname === '/' ? ' site__tab--on' : ''}`}
          aria-current={pathname === '/' ? 'page' : undefined}
        >
          <ChartIcon />
          <span>Dashboard</span>
        </Link>

        <Link
          to="/register"
          className={`site__tab${pathname.startsWith('/register') ? ' site__tab--on' : ''}`}
          aria-current={pathname.startsWith('/register') ? 'page' : undefined}
        >
          <GridIcon />
          <span>Vehicles</span>
        </Link>

        {/* Plant and equipment. A separate register because it carries no
            obligations — the Asset List has no insurance, fitness, permit, tax
            or PUC column for machinery — so there is nothing for the compliance
            columns to show. */}
        <Link
          to="/machinery"
          className={`site__tab${pathname.startsWith('/machinery') ? ' site__tab--on' : ''}`}
          aria-current={pathname.startsWith('/machinery') ? 'page' : undefined}
        >
          <GearIcon />
          <span>Machinery</span>
        </Link>
      </nav>

      <div className="site__actions">
        <IntakeBadge />

        {/*
          Ask.

          Beside the bell rather than as a tab: it is a thing you do and come
          back from, like import and report, not a place you live. A panel
          rather than a route for the same reason — the answer is most useful
          read against the screen you were already on, and its link takes you
          somewhere from there.
        */}
        <button
          type="button"
          className={`site__action${asking ? ' site__action--on' : ''}`}
          onClick={() => setAsking((v) => !v)}
          aria-expanded={asking}
          title="Ask a question about the fleet"
          aria-label="Ask a question about the fleet"
        >
          <AskIcon />
        </button>

        {/*
          The bell.

          It was a link to the rules screen and carried no badge, on the reasoning
          that "there is nothing here a count could honestly mean — the intake
          badge counts documents waiting for a person; reminders wait for nobody."

          That is no longer true, and the change is deliberate. The bell now opens
          the notifications: warnings that have actually been sent, which wait for
          a person to read and clear them. That IS something a count can honestly
          mean, so the badge is back — scoped to the signed-in user, because
          clearing is per person.
        */}
        <Link
          to="/notifications"
          className={`site__action${atNotifications ? ' site__action--on' : ''}`}
          title="Notifications — what has been warned about"
          aria-label={
            unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'
          }
          aria-current={atNotifications ? 'page' : undefined}
        >
          <BellIcon />
          {unread > 0 && (
            <span className="site__badge" aria-hidden="true">
              {/* Past ninety-nine the exact figure stops meaning anything and
                  starts breaking the circle it sits in. */}
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Link>

        {/* The real thing now — who is signed in, and the way out. */}
        <UserMenu />
      </div>

      {asking && <AskPanel onClose={() => setAsking(false)} />}
    </header>
  )
}

/**
 * How many emailed certificates are waiting for somebody to look at them.
 *
 * A real count, unlike the bell beside it — this one is a SELECT, not a guess,
 * which is the whole reason it is allowed to be a number. It renders nothing at
 * all when the queue is empty or no mailbox is connected: a persistent "0" is
 * chrome that trains people to ignore the spot where a real number will appear.
 *
 * Its own endpoint rather than a count over the queue, because this asks on
 * every page load and must not pay for the joins the review screen needs.
 */
function IntakeBadge() {
  const count = useQuery({
    queryKey: ['intake', 'count'],
    queryFn: api.getIntakeCount,
    // Long, and no window-focus refetch: this is a background nudge, not a
    // live counter, and it sits on every screen in the app.
    staleTime: 60_000,
    // Signed out, or the endpoint is unreachable — either way the header must
    // not surface an error. The badge simply is not there.
    retry: false,
  })

  const pending = count.data?.pending ?? 0
  if (pending === 0) return null

  const unmatched = count.data?.unmatched ?? 0

  return (
    <Link
      to="/intake"
      className="site__action site__action--intake"
      title={
        unmatched > 0
          ? `${pending} awaiting review, ${unmatched} with no vehicle matched`
          : `${pending} awaiting review`
      }
      aria-label={`Email intake: ${pending} certificates awaiting review`}
    >
      <TrayIcon />
      <span className="site__count">{pending > 99 ? '99+' : pending}</span>
    </Link>
  )
}

function ChartIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M3 15V8M7 15V4M11 15v-5M15 15V6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  )
}

function GridIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M2.5 4.5h13M2.5 9h13M2.5 13.5h13"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

function GearIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">
      <circle cx="9" cy="9" r="2.6" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M9 1.9v1.8M9 14.3v1.8M16.1 9h-1.8M3.7 9H1.9M14 4l-1.3 1.3M5.3 12.7L4 14M14 14l-1.3-1.3M5.3 5.3L4 4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

function TrayIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M2.5 10.5h3l1 2h5l1-2h3M2.5 10.5 4.3 4.2a1 1 0 0 1 .96-.7h7.48a1 1 0 0 1 .96.7l1.8 6.3M2.5 10.5v3a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

function BellIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M4.4 7.3a4.6 4.6 0 0 1 9.2 0c0 3.1.9 4.4 1.5 5a.5.5 0 0 1-.35.86H3.25a.5.5 0 0 1-.35-.86c.6-.6 1.5-1.9 1.5-5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M7.2 15.2a2 2 0 0 0 3.6 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

/**
 * The ask mark — a magnifier with a spark.
 *
 * Search rather than chat, because that is what the box actually does: it
 * answers from the register and does not converse. The spark is what separates
 * it from an ordinary filter control, and from the bell and intake marks beside
 * it in the same header.
 *
 * currentColor throughout, so it inherits the header's ink and its active state
 * without a second rule, exactly as the bell does.
 */
function AskIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="8.6" cy="8.6" r="5.4" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="m12.7 12.7 4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      {/* The spark. Four-pointed rather than a five-pointed star: it reads as a
          glint at 20px, where a star reads as a favourite. */}
      <path
        d="M16.1 1.4l.62 1.68 1.68.62-1.68.62-.62 1.68-.62-1.68-1.68-.62 1.68-.62.62-1.68Z"
        fill="currentColor"
      />
    </svg>
  )
}
